import { useCallback, useEffect, useMemo, useRef, useState, type SyntheticEvent } from 'react';
import { createFilterOptions, type AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import { useSelector } from 'react-redux';

import Autocomplete from 'components/Common/Autocomplete';
import { ParishesAutocompleteProps } from 'types/autocomplete';
import AutocompleteOption from 'components/Common/autocomplete/AutocompleteOption';
import { Parish, ParishPublic, normalizeParish } from 'types/parishes';
import type { Paginated } from 'api/v1Helpers';
import { debounce } from 'utils/helpers';
import { appClient } from 'api/apiClient';
import { buildPageQuery } from 'api/v1Helpers';
import { notify } from 'components/Common/Toast';
import { selectSelectedParishId } from 'redux/selectors/parishContext';

/**
 * Allowlist mode owns its own filtering. MUI's default `filterOptions` matches on
 * `getOptionLabel` — the parish name only — so a code or city term would be dropped even
 * though the loaded catalog holds it. `companyCode` is not listed: `normalizeParish` sets it
 * from the same `code` field.
 */
const filterAllowlistParishes = createFilterOptions<Parish>({
    stringify: (parish) => [parish.name, parish.code, parish.city].filter(Boolean).join(' '),
});

/**
 * A PROVIDER_ADMIN's `staffRoles` is expanded onto every descendant parish, so the allowlist
 * can be diocese-sized. Load it in bounded batches rather than one burst of N requests —
 * an API-call storm is this component's worst failure mode.
 */
const ALLOWLIST_FETCH_CONCURRENCY = 6;

/**
 * Local search only — does not dispatch fetchParishData (avoids overwriting Parishes page Redux).
 *
 * Default mode: tenant directory / children (`GET /v1/parishes`, `…/children`).
 * Allowlist mode (`allowedParishIds` set): load each UUID via `GET /v1/parishes/:id` into a
 * complete catalog, so every authorized parish stays selectable (not lost to pageSize=50) and
 * filtering needs no further network calls.
 */
const ParishesAutocomplete = (props: ParishesAutocompleteProps) => {
    const { allowedParishIds, value, defaultSearchText, ...autocompleteProps } = props;
    const selectedParishId = useSelector(selectSelectedParishId);
    const [loading, setLoading] = useState(false);
    const [parishes, setParishes] = useState<Parish[]>([]);
    const [allowlistCatalog, setAllowlistCatalog] = useState<Parish[]>([]);
    const [searchText, setSearchText] = useState('');
    // "Initial fetch attempted" — not "non-empty results". A ref keeps this out of the
    // useCallback dep graph so setParishes cannot re-trigger fetchParishes (API loop).
    const hasLoadedOnce = useRef(false);

    const allowlistMode = allowedParishIds != null;
    // A stable string key: `allowedParishIds` is a fresh array on most renders, and duplicate
    // parishId rows would otherwise cost a duplicate GET and a duplicate React key.
    const allowedKey = useMemo(() => {
        if (allowedParishIds == null) return null;
        return [...new Set(allowedParishIds)].sort().join(',');
    }, [allowedParishIds]);

    // --- Allowlist mode: one catalog load per allowed-id set; filtering is client-side. ---
    useEffect(() => {
        if (allowedKey === null) {
            setAllowlistCatalog([]);
            return;
        }

        let cancelled = false;
        const ids = allowedKey === '' ? [] : allowedKey.split(',');
        setAllowlistCatalog([]);

        (async () => {
            if (ids.length === 0) {
                if (!cancelled) {
                    setLoading(false);
                    hasLoadedOnce.current = true;
                }
                return;
            }

            setLoading(true);
            const loaded: Parish[] = [];
            let failed = 0;
            try {
                for (let start = 0; start < ids.length; start += ALLOWLIST_FETCH_CONCURRENCY) {
                    if (cancelled) return;
                    const batch = await Promise.all(
                        ids.slice(start, start + ALLOWLIST_FETCH_CONCURRENCY).map(async (id) => {
                            try {
                                const response = await appClient.get<ParishPublic>(`parishes/${id}`);
                                return normalizeParish(response);
                            } catch {
                                return null;
                            }
                        })
                    );
                    for (const parish of batch) {
                        if (parish) loaded.push(parish);
                        else failed += 1;
                    }
                }
                if (cancelled) return;
                setAllowlistCatalog(loaded);
                // A partial catalog silently hides parishes the user is entitled to — say so
                // rather than letting them conclude they have no access.
                if (failed) {
                    notify(`${failed} of your ${ids.length} parishes could not be loaded. Refresh to try again.`, 'error');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    hasLoadedOnce.current = true;
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [allowedKey]);

    // --- Directory mode (unchanged): search / children / first page. ---
    const fetchParishes = useCallback(async () => {
        if (allowlistMode) return;

        let search = searchText;
        if (!hasLoadedOnce.current && defaultSearchText) {
            search = defaultSearchText;
        }
        try {
            setLoading(true);
            const term = (search || '').trim();
            let items: Parish[] = [];
            if (term) {
                const qs = buildPageQuery(1, 50, { search: term });
                const response = await appClient.get<Paginated<ParishPublic>>(`parishes?${qs}`);
                items = (response?.items || []).map(normalizeParish);
            } else if (selectedParishId) {
                const response = await appClient.get<Paginated<ParishPublic>>(`parishes/${selectedParishId}/children`);
                items = (response?.items || []).map(normalizeParish);
            } else {
                const qs = buildPageQuery(1, 50);
                const response = await appClient.get<Paginated<ParishPublic>>(`parishes?${qs}`);
                items = (response?.items || []).map(normalizeParish);
            }
            setParishes(items);
            hasLoadedOnce.current = true;
        } catch (_e) {
            setParishes([]);
            // Count a failed attempt as loaded so a persistent error cannot retry-storm.
            hasLoadedOnce.current = true;
        } finally {
            setLoading(false);
        }
    }, [allowlistMode, searchText, defaultSearchText, selectedParishId]);

    useEffect(() => {
        if (allowlistMode) return;
        fetchParishes();
    }, [allowlistMode, fetchParishes]);

    const noOptionsText = allowlistMode
        ? loading
            ? 'Loading...'
            : allowlistCatalog.length
              ? 'No matching staffed parish'
              : 'No staffed parishes available'
        : searchText && loading
          ? 'Loading...'
          : searchText
            ? 'No options'
            : 'Please type to search...';

    return (
        <Autocomplete
            loading={loading}
            options={allowlistMode ? allowlistCatalog : parishes}
            value={value}
            // A value that has not resolved to an option yet (still loading, or a stale prefill)
            // is a raw code string — render it rather than returning undefined, which makes MUI
            // blank the field and log `getOptionLabel ... returned undefined`.
            getOptionLabel={(option) => (loading ? 'Loading...' : typeof option === 'string' ? option : option?.name || '')}
            placeholder={allowlistMode ? 'Select a parish you staff…' : 'Please type to search...'}
            renderOption={(props, option: Parish) => <AutocompleteOption option={{ ...option, imgUrl: option?.logoUrl }} props={props} />}
            getOptionKey={(option) => (typeof option === 'object' ? option?.id || option?.companyId : option)}
            noOptionsText={noOptionsText}
            {...(allowlistMode ? { filterOptions: filterAllowlistParishes } : null)}
            onInputChange={debounce((_: SyntheticEvent, nextSearch: string, reason: AutocompleteInputChangeReason) => {
                if (reason === 'input') {
                    setSearchText(nextSearch);
                } else if (reason === 'clear') {
                    setSearchText('');
                }
            }, 700)}
            {...autocompleteProps}
        />
    );
};

export default ParishesAutocomplete;
